import { createRouter, RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized, createWebHashHistory } from 'vue-router';

import store from "@/store";
import axios from "axios";
import { CurrentUserViewModel } from "@/store/classes";
import Login from '@/views/Login.vue3.vue';
import ResetLogin from '@/views/ResetLogin.vue3.vue';
import PortalInviteView from "@/views/portal/PortalInviteView.vue3.vue";

const routes: Array<RouteRecordRaw> = [
    {
        path: '/login',
        name: 'login',
        component: Login,
        props: {
            portalLogin: true
        },
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/login/reset/:resetKey',
        name: 'login-reset-view',
        component: ResetLogin,
        props: true,
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/invite/:inviteKey',
        name: 'invite-view',
        component: PortalInviteView,
        props: true,
        meta: { fullscreen: true }
    },

    // patient portal
    {
        path: '/',
        component: () => import(/* webpackChunkName: "patientPortal" */ '../views/portal/patient/PatientPortalIndex.vue3.vue'),
    },
    {
        path: '/home',
        name: 'patient-portal-index',
        component: () => import(/* webpackChunkName: "patientPortal" */ '../views/portal/patient/PatientPortalIndex.vue3.vue'),
    },
    {
        path: '/messages',
        name: 'patient-messages',
        component: () => import(/* webpackChunkName: "patientPortal" */ '../views/portal/patient/PatientPortalMessagesView.vue3.vue'),
    },
    {
        path: '/visits/clinical-notes',
        name: 'visit-clinical-notes',
        component: () => import(/* webpackChunkName: "patientPortal" */ '../views/portal/patient/PatientClinicalNotesIndex.vue3.vue'),
    },
    {
        path: '/visits/upcoming',
        name: 'visit-upcoming-index',
        component: () => import(/* webpackChunkName: "patientPortal" */ '../views/portal/patient/PatientVisitIndex.vue3.vue'),
    },
    {
        path: '/visits/requests',
        name: 'visit-requests-index',
        component: () => import(/* webpackChunkName: "patientPortal" */ '../views/portal/patient/PatientVisitRequestIndex.vue3.vue'),
    },
    {
        path: '/visits/requests/create',
        name: 'visit-requests-create',
        component: () => import(/* webpackChunkName: "patientPortal" */ '../views/portal/patient/PatientPortalVisitRequestView.vue3.vue'),
    },
    {
        path: '/settings',
        name: 'settings',
        component: () => import(/* webpackChunkName: "patientPortal" */ '../views/portal/patient/PatientSettingsView.vue3.vue'),
    },

    // public portal pages
    {
        path: '/:facilityKey/surveys/:surveyKey',
        name: 'portal-survey',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/PortalSurveyView.vue3.vue'),
        props: true,
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/survey-response/:messageGuid',
        name: 'portal-survey-response',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/PortalSurveyResponseView.vue3.vue'),
        props: true,
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/calendar-add/:messageGuid',
        name: 'calendar-add',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/PortalCalendarAdd.vue3.vue'),
        props: (route) => ({ ...route.query, messageGuid: route.params.messageGuid }),
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/visit-request/:patientKey',
        name: 'portal-visit-request',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/PortalVisitRequestView.vue3.vue'),
        props: true,
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/subscribe/:patientKey',
        name: 'portal-subscribe',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/PortalSubscribeView.vue3.vue'),
        props: true,
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/settings/:patientKey',
        name: 'portal-settings',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/PortalContactSettingsView.vue3.vue'),
        props: true,
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/intake/visit/:patientKey',
        name: 'portal-visit-intake',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/PortalVisitIntakeView.vue3.vue'),
        props: true,
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/intake/visit',
        name: 'facility-visit-intake',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/PortalFacilityVisitIntakeView.vue3.vue'),
        props: true,
        meta: { fullscreen: true }
    },
    {
        path: '/:facilityKey/intake/:patientKey',
        name: 'portal-intake',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/PortalIntakeView.vue3.vue'),
        props: true,
        meta: { fullscreen: true }
    },
    {
        path: '/privacy-policy',
        name: 'privacy-policy',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/policies/PrivacyPolicy.vue3.vue'),
        meta: { fullscreen: true }
    },
    {
        path: '/terms-and-conditions',
        name: 'terms-and-conditions',
        component: () => import(/* webpackChunkName: "portal" */ '../views/portal/policies/TermsAndConditions.vue3.vue'),
        meta: { fullscreen: true }
    },
];

const router = createRouter({
    history: createWebHashHistory(process.env.BASE_URL),
    routes
});

const publicRoutes = [
    'calendar-add',
    'invite-view',
    'login-reset-view',
    'facility-visit-intake',
    'portal-visit-request',
    'portal-survey',
    'portal-survey-response',
    'portal-subscribe',
    'portal-settings',
    'portal-visit-intake',
    'portal-intake',
    'terms-and-conditions',
    'privacy-policy'
];

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    if (to.path === '/login' || (!!to.name && typeof to.name === 'string' && publicRoutes.indexOf(to.name) > -1) || store.state['initialized']) {
        next();
    } else if (!window.localStorage.getItem(process.env.VUE_APP_ACCESS_TOKEN_KEY || '')
        || !window.localStorage.getItem(process.env.VUE_APP_FACILITY_ID_KEY || '')) {
        // no token to initialize, send to login
        if (from.path === '/login') {
            next(false);
        } else {
            next('/login');
        }
    } else {
        // initialize from local storage (this happens with refreshes)
        try {
            const storageToken = window.localStorage.getItem(process.env.VUE_APP_ACCESS_TOKEN_KEY || '');
            const storageFacilityId = window.localStorage.getItem(process.env.VUE_APP_FACILITY_ID_KEY || '');
            if (!storageToken || !storageFacilityId) {
                next('/login');
                return;
            }
            await store.dispatch('setAccessToken', JSON.parse(storageToken));
            await store.dispatch('setFacilityId', JSON.parse(storageFacilityId));
            const userResponse = await axios.get<CurrentUserViewModel>(`/auth/user/current/`);
            await store.dispatch('setPatientId', userResponse.data.contact.id);
            await store.dispatch('initializePatientPortal');
            next();
        } catch {
            next('/login');
        }
    }
});

export default router;
